import {
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  LOD,
  Sphere,
  type BufferGeometry,
  type Camera,
  type PerspectiveCamera,
  type ShaderMaterial,
} from 'three';

/** One kind of detail: `count` instances, each a column-major 4x4 matrix and the edge id its lines take. */
export interface NearDetailSet<K extends string> {
  readonly kind: K;
  readonly count: number;
  readonly matrices: Float32Array;
  readonly edgeIds: Float32Array;
}

/**
 * A near-detail LOD draws its instances within `radius` of the camera and in view, and all round the
 * camera closer than `near`. It takes the camera's position to cells whose corners lie `move` / 2 from
 * their centre, and its view direction to steps of `turn` in yaw and pitch, and draws the set chosen for
 * the cell's centre and the step's middle. So the set changes only when the camera crosses into another
 * cell or step, reaches at most `move` past the radius, and a still frame draws exactly what a live
 * frame at the same pose draws.
 */
export interface NearDetailSpec {
  /** The root's name; its meshes are named `${name}-${kind}`. */
  readonly name: string;
  readonly radius: number;
  readonly move?: number;
  readonly turn?: number;
  readonly near?: number;
}

/** The steps of the houses' joinery (LOD1), and the default of every near-detail LOD. */
export const NEAR_DETAIL = { move: 20, turn: (10 * Math.PI) / 180, near: 60 } as const;

/** `root` goes into the scene; the renderer updates it with each frame's camera before it draws the meshes. */
export interface NearDetailLod {
  readonly root: LOD;
  readonly meshes: readonly InstancedMesh[];
}

interface Steps {
  readonly radius: number;
  readonly move: number;
  /** Edge of a position cell: its half-diagonal is `move` / 2. */
  readonly cell: number;
  readonly turn: number;
  readonly near: number;
}

const CELL = 64;

/** Instances filed by ground cell: the cell's items run from start[cell] to start[cell + 1]. */
interface Grid {
  readonly minX: number;
  readonly minZ: number;
  readonly cols: number;
  readonly rows: number;
  readonly start: Int32Array;
  readonly items: Int32Array;
}

interface Layer {
  readonly mesh: InstancedMesh;
  readonly matrices: Float32Array;
  readonly edgeIds: Float32Array;
  readonly centres: Float32Array;
  readonly grid: Grid;
  readonly ids: InstancedBufferAttribute;
  readonly bounds: Sphere;
  /** The farthest any instance reaches from its centre. */
  readonly pad: number;
}

function largestColumn(m: Float32Array, a: number): number {
  let largest = 0;
  for (let c = 0; c < 3; c++) largest = Math.max(largest, Math.hypot(m[a + c * 4]!, m[a + c * 4 + 1]!, m[a + c * 4 + 2]!));
  return largest;
}

/* An instance's centre is its template's bounding-sphere centre carried by its matrix. */
function centresOf<K extends string>(set: NearDetailSet<K>, sphere: Sphere): { centres: Float32Array; pad: number } {
  const m = set.matrices;
  const { x, y, z } = sphere.center;
  const centres = new Float32Array(set.count * 3);
  let pad = 0;
  for (let i = 0; i < set.count; i++) {
    const a = i * 16;
    for (let k = 0; k < 3; k++) centres[i * 3 + k] = m[a + 12 + k]! + m[a + k]! * x + m[a + 4 + k]! * y + m[a + 8 + k]! * z;
    pad = Math.max(pad, sphere.radius * largestColumn(m, a));
  }
  return { centres, pad };
}

function gridOf(centres: Float32Array): Grid {
  const count = centres.length / 3;
  let [minX, minZ, maxX, maxZ] = [Infinity, Infinity, -Infinity, -Infinity];
  for (let i = 0; i < count; i++) {
    minX = Math.min(minX, centres[i * 3]!);
    maxX = Math.max(maxX, centres[i * 3]!);
    minZ = Math.min(minZ, centres[i * 3 + 2]!);
    maxZ = Math.max(maxZ, centres[i * 3 + 2]!);
  }
  if (count === 0) [minX, minZ, maxX, maxZ] = [0, 0, 0, 0];
  const cols = Math.floor((maxX - minX) / CELL) + 1;
  const rows = Math.floor((maxZ - minZ) / CELL) + 1;
  const cellOf = (i: number) =>
    Math.floor((centres[i * 3]! - minX) / CELL) + Math.floor((centres[i * 3 + 2]! - minZ) / CELL) * cols;
  const start = new Int32Array(cols * rows + 1);
  for (let i = 0; i < count; i++) start[cellOf(i) + 1]!++;
  for (let c = 0; c < cols * rows; c++) start[c + 1]! += start[c]!;
  const next = start.slice(0, cols * rows);
  const items = new Int32Array(count);
  for (let i = 0; i < count; i++) items[next[cellOf(i)]!++] = i;
  return { minX, minZ, cols, rows, start, items };
}

function templateSphere(geometry: BufferGeometry): Sphere {
  if (geometry.boundingSphere === null) geometry.computeBoundingSphere();
  return geometry.boundingSphere!;
}

function layerOf<K extends string>(set: NearDetailSet<K>, geometry: BufferGeometry, material: ShaderMaterial, name: string): Layer {
  const ids = new InstancedBufferAttribute(new Float32Array(set.count), 1).setUsage(DynamicDrawUsage);
  geometry.setAttribute('aInstanceEdgeId', ids);
  const mesh = new InstancedMesh(geometry, material, set.count);
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  mesh.count = 0;
  mesh.visible = false;
  mesh.frustumCulled = false;
  mesh.name = `${name}-${set.kind}`;
  /* The app turns frustum culling on for every painted mesh after the painter's view, and an instanced
     mesh computes its bounds once; these follow each set instead. */
  const bounds = new Sphere();
  mesh.boundingSphere = bounds;
  const { centres, pad } = centresOf(set, templateSphere(geometry));
  return { mesh, matrices: set.matrices, edgeIds: set.edgeIds, centres, grid: gridOf(centres), ids, bounds, pad };
}

/** Half the angle across the view's diagonal; a camera without a field of view sees all round. */
function halfDiagonal(camera: Camera): number {
  const perspective = camera as PerspectiveCamera;
  if (!perspective.isPerspectiveCamera) return Math.PI;
  const t = Math.tan((perspective.fov * Math.PI) / 360) / perspective.zoom;
  return Math.atan(t * Math.sqrt(1 + perspective.aspect * perspective.aspect));
}

/* Camera key (cell x, y, z, yaw step, pitch step, half view angle) and chosen pose (x, y, z, forward
   x, y, z); typed so frames allocate nothing. */
const [KX, KY, KZ, KYAW, KPITCH, KHALF] = [0, 1, 2, 3, 4, 5];
const [X, Y, Z, FX, FY, FZ] = [0, 1, 2, 3, 4, 5];

function keyOf(camera: Camera, steps: Steps, out: Float64Array): void {
  const e = camera.matrixWorld.elements;
  const length = Math.hypot(e[8], e[9], e[10]);
  out[KX] = Math.floor(e[12] / steps.cell);
  out[KY] = Math.floor(e[13] / steps.cell);
  out[KZ] = Math.floor(e[14] / steps.cell);
  out[KYAW] = Math.round(Math.atan2(-e[8], -e[10]) / steps.turn);
  out[KPITCH] = Math.round(Math.asin(Math.max(-1, Math.min(1, -e[9] / length))) / steps.turn);
  out[KHALF] = halfDiagonal(camera);
}

function sameKey(a: Float64Array, b: Float64Array): boolean {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/* The pose a key's set is chosen for: the cell's centre, looking along the step's middle. */
function poseOf(key: Float64Array, steps: Steps, out: Float64Array): void {
  out[X] = (key[KX]! + 0.5) * steps.cell;
  out[Y] = (key[KY]! + 0.5) * steps.cell;
  out[Z] = (key[KZ]! + 0.5) * steps.cell;
  const yaw = key[KYAW]! * steps.turn;
  const pitch = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, key[KPITCH]! * steps.turn));
  out[FX] = Math.cos(pitch) * Math.sin(yaw);
  out[FY] = Math.sin(pitch);
  out[FZ] = Math.cos(pitch) * Math.cos(yaw);
}

interface Reach {
  readonly reach: number;
  readonly near: number;
  readonly cosLimit: number;
}

/*
 * The camera stands within half a cell's diagonal (`slack`) of the chosen pose and looks within `turn`
 * of it (half a step in yaw, half in pitch). So the set reaches `slack` past the radius, and past `near`
 * it widens the view by `turn` and by the most `slack` subtends there.
 */
function reachOf(half: number, steps: Steps): Reach {
  const slack = steps.move / 2;
  const sweep = slack >= steps.near ? Math.PI : Math.asin(slack / steps.near);
  const limit = half + steps.turn + sweep;
  return { reach: steps.radius + slack, near: steps.near, cosLimit: limit >= Math.PI ? -1 : Math.cos(limit) };
}

function inView(centres: Float32Array, i: number, pose: Float64Array, r: Reach): boolean {
  const dx = centres[i * 3]! - pose[X]!;
  const dy = centres[i * 3 + 1]! - pose[Y]!;
  const dz = centres[i * 3 + 2]! - pose[Z]!;
  const d2 = dx * dx + dy * dy + dz * dz;
  if (d2 > r.reach * r.reach) return false;
  if (d2 <= r.near * r.near) return true;
  return dx * pose[FX]! + dy * pose[FY]! + dz * pose[FZ]! >= Math.sqrt(d2) * r.cosLimit;
}

function cellAt(v: number, min: number, cells: number): number {
  return Math.min(cells - 1, Math.max(0, Math.floor((v - min) / CELL)));
}

function fillCell(layer: Layer, cell: number, pose: Float64Array, r: Reach, from: number): number {
  const { grid, centres, matrices, edgeIds } = layer;
  const target = layer.mesh.instanceMatrix.array as Float32Array;
  const ids = layer.ids.array as Float32Array;
  let count = from;
  for (let k = grid.start[cell]!; k < grid.start[cell + 1]!; k++) {
    const i = grid.items[k]!;
    if (!inView(centres, i, pose, r)) continue;
    for (let e = 0; e < 16; e++) target[count * 16 + e] = matrices[i * 16 + e]!;
    ids[count] = edgeIds[i]!;
    count++;
  }
  return count;
}

function commit(layer: Layer, count: number, pose: Float64Array, r: Reach): void {
  const { mesh, ids } = layer;
  mesh.count = count;
  mesh.visible = count > 0;
  layer.bounds.center.set(pose[X]!, pose[Y]!, pose[Z]);
  layer.bounds.radius = r.reach + layer.pad;
  if (count === 0) return;
  mesh.instanceMatrix.clearUpdateRanges();
  mesh.instanceMatrix.addUpdateRange(0, count * 16);
  mesh.instanceMatrix.needsUpdate = true;
  ids.clearUpdateRanges();
  ids.addUpdateRange(0, count);
  ids.needsUpdate = true;
}

function fill(layer: Layer, pose: Float64Array, r: Reach): void {
  const { grid } = layer;
  const c0 = cellAt(pose[X]! - r.reach, grid.minX, grid.cols);
  const c1 = cellAt(pose[X]! + r.reach, grid.minX, grid.cols);
  const r0 = cellAt(pose[Z]! - r.reach, grid.minZ, grid.rows);
  const r1 = cellAt(pose[Z]! + r.reach, grid.minZ, grid.rows);
  let count = 0;
  for (let row = r0; row <= r1; row++) {
    for (let col = c0; col <= c1; col++) count = fillCell(layer, col + row * grid.cols, pose, r, count);
  }
  commit(layer, count, pose, r);
}

function hide(layer: Layer): void {
  layer.mesh.count = 0;
  layer.mesh.visible = false;
}

/*
 * A LOD, because the renderer calls a LOD's update with the camera it is about to draw with, before it
 * draws the LOD's children; choosing the set there is what makes still frames match live ones.
 */
class NearDetailRoot extends LOD {
  private readonly select: (camera: Camera) => void;

  constructor(name: string, select: (camera: Camera) => void) {
    super();
    this.name = name;
    this.select = select;
  }

  override update(camera: Camera): void {
    this.select(camera);
  }
}

function painterAmount(material: ShaderMaterial): number {
  const amount: unknown = material.uniforms['uPainterAmount']?.value;
  return typeof amount === 'number' ? amount : 0;
}

function templateOf<K extends string>(templates: ReadonlyMap<K, BufferGeometry>, kind: K, name: string): BufferGeometry {
  const template = templates.get(kind);
  if (!template) throw new Error(`near-detail LOD "${name}": no template for kind "${kind}"`);
  return template;
}

/**
 * One instanced mesh per kind. `material` must be the instanced painted material: its painter-projection
 * amount hides every detail, since the painter's view shows LOD0 only.
 */
export function buildNearDetailLod<K extends string>(
  sets: readonly NearDetailSet<K>[],
  templates: ReadonlyMap<K, BufferGeometry>,
  material: ShaderMaterial,
  spec: NearDetailSpec
): NearDetailLod {
  const move = spec.move ?? NEAR_DETAIL.move;
  const steps: Steps = {
    radius: spec.radius,
    move,
    cell: move / Math.sqrt(3),
    turn: spec.turn ?? NEAR_DETAIL.turn,
    near: spec.near ?? NEAR_DETAIL.near,
  };
  const layers = sets.map((set) => layerOf(set, templateOf(templates, set.kind, spec.name), material, spec.name));
  const key = new Float64Array(6);
  const last = new Float64Array(6).fill(Number.NaN);
  const pose = new Float64Array(6);
  const select = (camera: Camera): void => {
    if (painterAmount(material) > 0) {
      for (const layer of layers) hide(layer);
      last.fill(Number.NaN);
      return;
    }
    keyOf(camera, steps, key);
    if (sameKey(key, last)) return;
    last.set(key);
    poseOf(key, steps, pose);
    const r = reachOf(key[KHALF]!, steps);
    for (const layer of layers) fill(layer, pose, r);
  };
  const root = new NearDetailRoot(spec.name, select);
  for (const layer of layers) root.add(layer.mesh);
  return { root, meshes: layers.map((l) => l.mesh) };
}
